package dev.kuml.portal.web.i18n.strings

public interface AuthStrings {
    public val loginTab: String
    public val registerTab: String
    public val emailLabel: String
    public val passwordLabel: String
    public val confirmPasswordLabel: String
    public val emailInvalid: String
    public val passwordRequired: String

    /** Shown by `AuthScreen.validatePassword()` in REGISTER mode when the entered password is
     * non-empty but shorter than the server's minimum (`UserService.MIN_PASSWORD_LENGTH` = 8) --
     * a client-side pre-empt so the register form never round-trips to the server just to bounce
     * back the same "too short" rejection (see R-18-major-finding-1's post-mortem). */
    public val passwordTooShort: String
    public val passwordsDoNotMatch: String
    public val submitLogin: String
    public val submitRegister: String
    public val submitLoginBusy: String
    public val submitRegisterBusy: String
    public val wrongCredentials: String
    public val accountAlreadyExists: String
    public val genericAuthFailure: String

    /** Silent displayName fallback derived at registration time (`AuthScreen`'s KDoc) -- follows
     * whichever language was active at registration, then is stored server-side and editable in
     * the profile later (R-23). */
    public val defaultDisplayName: String

    // ---------- Email infrastructure wave: R-3 (verify-email) / R-4 (forgot/reset password) ----------

    /** LOGIN-mode-only link below the password field (`AuthScreen.applyMode` hides it in REGISTER
     * mode) -- opens the forgot-password mini form in place of the login/register fields
     * (`AuthScreen.showForgotPassword`). */
    public val forgotPasswordLink: String
    public val forgotPasswordTitle: String
    public val forgotPasswordIntro: String
    public val forgotPasswordSubmit: String
    public val forgotPasswordSubmitBusy: String

    /** Shown on every non-network outcome of `POST /api/auth/password-reset/request` --
     * deliberately the SAME copy whether or not the address belongs to a real account (see
     * `dev.kuml.portal.server.auth.passwordResetRoutes`'s "No account-enumeration leak" KDoc): the
     * client must not let its own wording become the leak that server route was built to avoid. */
    public val forgotPasswordSuccess: String
    public val backToLoginLink: String

    /** `dev.kuml.portal.web.auth.verifyEmailScreen` (`/verify-email/:token`, R-3). */
    public val verifyingTitle: String
    public val verifyingMessage: String
    public val verifySuccessTitle: String
    public val verifySuccessMessage: String
    public val verifyFailureTitle: String
    public val verifyFailureMessage: String
    public val continueToLogin: String

    /** `dev.kuml.portal.web.auth.resetPasswordScreen` (`/reset-password/:token`, R-4). */
    public val resetPasswordTitle: String
    public val newPasswordLabel: String
    public val submitResetPassword: String
    public val submitResetPasswordBusy: String
    public val resetPasswordSuccessTitle: String
    public val resetPasswordSuccessMessage: String
    public val resetPasswordFailureTitle: String
    public val resetPasswordFailureMessage: String
    public val requestNewResetLink: String

    /** `dev.kuml.portal.web.components.emailVerificationBanner` -- shown on the project list for a
     * caller whose `UserResponse.emailVerified` is `false`. */
    public val verifyBannerMessage: String
    public val verifyBannerResend: String
    public val verifyBannerResendBusy: String
    public val verifyBannerResendSuccess: String

    // ---------- R-5: Social Login ----------

    /** Separator label between the email/password form and the social-login buttons
     * (`AuthScreen`'s `kportal-auth-divider`). */
    public val orDivider: String
    public val signInWithGoogle: String
    public val signInWithGitHub: String

    /** `?oauth=conflict` -- the address already belongs to a password account (R-5 plan decision
     * E-4): sign in with the password instead, then link the provider from the profile page. */
    public val oauthConflictNotice: String

    /** `?oauth=no_email` -- the provider offered no usable address. */
    public val oauthNoEmailNotice: String

    /** `?oauth=cancelled` -- the caller declined consent at the provider. */
    public val oauthCancelledNotice: String

    /** `?oauth=unavailable` -- this provider is not configured on the server (e.g. local dev). */
    public val oauthUnavailableNotice: String

    /** `?oauth=error` -- every other callback failure (missing/expired/replayed state, provider
     * mismatch, a malformed provider response). */
    public val oauthGenericError: String

    /** `?oauth=link_session_lost` -- the LINK flow's session cookie was missing or belonged to a
     * different account by the time the callback returned. */
    public val oauthLinkSessionLost: String

    /** Every plain (non-parameterised) string this interface declares -- declared ONCE here so
     * both implementations inherit the same list and the completeness test cannot drift per
     * language. Adding a property above without adding it here is the only failure mode, and it is
     * a single co-located line in the same file. */
    public val allPlainStrings: List<String>
        get() =
            listOf(
                loginTab,
                registerTab,
                emailLabel,
                passwordLabel,
                confirmPasswordLabel,
                emailInvalid,
                passwordRequired,
                passwordTooShort,
                passwordsDoNotMatch,
                submitLogin,
                submitRegister,
                submitLoginBusy,
                submitRegisterBusy,
                wrongCredentials,
                accountAlreadyExists,
                genericAuthFailure,
                defaultDisplayName,
                forgotPasswordLink,
                forgotPasswordTitle,
                forgotPasswordIntro,
                forgotPasswordSubmit,
                forgotPasswordSubmitBusy,
                forgotPasswordSuccess,
                backToLoginLink,
                verifyingTitle,
                verifyingMessage,
                verifySuccessTitle,
                verifySuccessMessage,
                verifyFailureTitle,
                verifyFailureMessage,
                continueToLogin,
                resetPasswordTitle,
                newPasswordLabel,
                submitResetPassword,
                submitResetPasswordBusy,
                resetPasswordSuccessTitle,
                resetPasswordSuccessMessage,
                resetPasswordFailureTitle,
                resetPasswordFailureMessage,
                requestNewResetLink,
                verifyBannerMessage,
                verifyBannerResend,
                verifyBannerResendBusy,
                verifyBannerResendSuccess,
                orDivider,
                signInWithGoogle,
                signInWithGitHub,
                oauthConflictNotice,
                oauthNoEmailNotice,
                oauthCancelledNotice,
                oauthUnavailableNotice,
                oauthGenericError,
                oauthLinkSessionLost,
            )
}

public object AuthStringsDe : AuthStrings {
    override val loginTab = "Anmelden"
    override val registerTab = "Registrieren"
    override val emailLabel = "E-Mail"
    override val passwordLabel = "Passwort"
    override val confirmPasswordLabel = "Passwort bestätigen"
    override val emailInvalid = "Bitte geben Sie eine gültige E-Mail-Adresse ein."
    override val passwordRequired = "Bitte geben Sie Ihr Passwort ein."
    override val passwordTooShort = "Das Passwort muss mindestens 8 Zeichen lang sein."
    override val passwordsDoNotMatch = "Die Passwörter stimmen nicht überein."
    override val submitLogin = "Anmelden"
    override val submitRegister = "Konto erstellen"
    override val submitLoginBusy = "Anmelden…"
    override val submitRegisterBusy = "Konto wird erstellt…"
    override val wrongCredentials = "E-Mail oder Passwort ist nicht korrekt."
    override val accountAlreadyExists = "Für diese E-Mail besteht bereits ein Konto. Bitte melden Sie sich an."
    override val genericAuthFailure = "Anmeldung derzeit nicht möglich. Bitte versuchen Sie es erneut."
    override val defaultDisplayName = "Nutzer"

    override val forgotPasswordLink = "Passwort vergessen?"
    override val forgotPasswordTitle = "Passwort zurücksetzen"
    override val forgotPasswordIntro =
        "Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen des Passworts."
    override val forgotPasswordSubmit = "Link anfordern"
    override val forgotPasswordSubmitBusy = "Wird gesendet…"
    override val forgotPasswordSuccess =
        "Falls für diese E-Mail-Adresse ein Konto besteht, haben wir eine E-Mail mit einem Link zum Zurücksetzen gesendet."
    override val backToLoginLink = "Zurück zur Anmeldung"

    override val verifyingTitle = "E-Mail-Adresse wird bestätigt"
    override val verifyingMessage = "Einen Moment bitte, wir bestätigen Ihre E-Mail-Adresse …"
    override val verifySuccessTitle = "E-Mail-Adresse bestätigt"
    override val verifySuccessMessage = "Ihre E-Mail-Adresse wurde erfolgreich bestätigt."
    override val verifyFailureTitle = "Bestätigung fehlgeschlagen"
    override val verifyFailureMessage = "Dieser Bestätigungslink ist ungültig oder abgelaufen."
    override val continueToLogin = "Weiter zur Anmeldung"

    override val resetPasswordTitle = "Neues Passwort festlegen"
    override val newPasswordLabel = "Neues Passwort"
    override val submitResetPassword = "Passwort speichern"
    override val submitResetPasswordBusy = "Wird gespeichert…"
    override val resetPasswordSuccessTitle = "Passwort geändert"
    override val resetPasswordSuccessMessage = "Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt anmelden."
    override val resetPasswordFailureTitle = "Link ungültig"
    override val resetPasswordFailureMessage = "Dieser Link zum Zurücksetzen des Passworts ist ungültig oder abgelaufen."
    override val requestNewResetLink = "Neuen Link anfordern"

    override val verifyBannerMessage = "Bitte bestätigen Sie Ihre E-Mail-Adresse."
    override val verifyBannerResend = "E-Mail erneut senden"
    override val verifyBannerResendBusy = "Wird gesendet…"
    override val verifyBannerResendSuccess = "Bestätigungs-E-Mail wurde gesendet."

    override val orDivider = "oder"
    override val signInWithGoogle = "Mit Google anmelden"
    override val signInWithGitHub = "Mit GitHub anmelden"
    override val oauthConflictNotice =
        "Für diese E-Mail-Adresse besteht bereits ein Konto. Bitte melden Sie sich mit Ihrem Passwort an und " +
            "verknüpfen Sie den Anbieter anschließend in Ihrem Profil."
    override val oauthNoEmailNotice =
        "Von diesem Anbieter konnte keine nutzbare E-Mail-Adresse abgerufen werden. Bitte hinterlegen Sie dort " +
            "eine Adresse oder registrieren Sie sich mit E-Mail und Passwort."
    override val oauthCancelledNotice = "Die Anmeldung wurde abgebrochen."
    override val oauthUnavailableNotice = "Diese Anmeldeart ist derzeit nicht verfügbar."
    override val oauthGenericError = "Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut."
    override val oauthLinkSessionLost = "Ihre Sitzung ist zwischenzeitlich abgelaufen. Bitte melden Sie sich erneut an."
}

public object AuthStringsEn : AuthStrings {
    override val loginTab = "Log in"
    override val registerTab = "Sign up"
    override val emailLabel = "Email"
    override val passwordLabel = "Password"
    override val confirmPasswordLabel = "Confirm password"
    override val emailInvalid = "Please enter a valid email address."
    override val passwordRequired = "Please enter your password."
    override val passwordTooShort = "The password must be at least 8 characters."
    override val passwordsDoNotMatch = "The passwords do not match."
    override val submitLogin = "Log in"
    override val submitRegister = "Create account"
    override val submitLoginBusy = "Logging in…"
    override val submitRegisterBusy = "Creating account…"
    override val wrongCredentials = "Email or password is incorrect."
    override val accountAlreadyExists = "An account already exists for this email. Please log in."
    override val genericAuthFailure = "Sign-in is currently unavailable. Please try again."
    override val defaultDisplayName = "User"

    override val forgotPasswordLink = "Forgot your password?"
    override val forgotPasswordTitle = "Reset your password"
    override val forgotPasswordIntro = "Enter your email address and we'll send you a link to reset your password."
    override val forgotPasswordSubmit = "Send reset link"
    override val forgotPasswordSubmitBusy = "Sending…"
    override val forgotPasswordSuccess = "If an account exists for that email address, we've sent an email with a reset link."
    override val backToLoginLink = "Back to login"

    override val verifyingTitle = "Verifying your email address"
    override val verifyingMessage = "One moment, we're verifying your email address…"
    override val verifySuccessTitle = "Email address verified"
    override val verifySuccessMessage = "Your email address has been verified successfully."
    override val verifyFailureTitle = "Verification failed"
    override val verifyFailureMessage = "This verification link is invalid or has expired."
    override val continueToLogin = "Continue to login"

    override val resetPasswordTitle = "Set a new password"
    override val newPasswordLabel = "New password"
    override val submitResetPassword = "Save password"
    override val submitResetPasswordBusy = "Saving…"
    override val resetPasswordSuccessTitle = "Password changed"
    override val resetPasswordSuccessMessage = "Your password has been changed successfully. You can now log in."
    override val resetPasswordFailureTitle = "Invalid link"
    override val resetPasswordFailureMessage = "This password reset link is invalid or has expired."
    override val requestNewResetLink = "Request a new link"

    override val verifyBannerMessage = "Please verify your email address."
    override val verifyBannerResend = "Resend email"
    override val verifyBannerResendBusy = "Sending…"
    override val verifyBannerResendSuccess = "Verification email sent."

    override val orDivider = "or"
    override val signInWithGoogle = "Sign in with Google"
    override val signInWithGitHub = "Sign in with GitHub"
    override val oauthConflictNotice =
        "An account already exists for this email address. Please log in with your password and link this " +
            "provider from your profile afterwards."
    override val oauthNoEmailNotice =
        "No usable email address could be retrieved from this provider. Please set one there, or sign up with " +
            "email and password instead."
    override val oauthCancelledNotice = "Sign-in was cancelled."
    override val oauthUnavailableNotice = "This sign-in method is currently unavailable."
    override val oauthGenericError = "Sign-in failed. Please try again."
    override val oauthLinkSessionLost = "Your session has since expired. Please log in again."
}
